/**
*  The abstractElement mixin
*  @module framework/mixins/abstractElement
*/

import * as manifest  from '../instance/manifest';


/**
* Extends basic properties for dealing with elements
* @mixin
*/
export default {
    /**
     * The applicationInstance is put onto the global Vue constance by the x-generator
     * component.  All descendants can then pull it off of the context.  This instance has
     * some properties such as the list of registered components and registered interactions.
     */
    inject: [
        'applicationInstance',
    ],
    props: {
        /**
         * The node from the manifest.
         */
        nodeManifest: Object,

        /**
         * The control id.
         */
        dataControlId: String,

        /**
         * A string of classes that can be applied to the component.
         */
        dataClass: String,
    },
    computed: {
        /**
         * The control id.
         * @returns {String} controlId
         */
        controlId() {
            if (this.dataControlId) {
                return this.dataControlId;
            }

            if (this.nodeManifest && this.nodeManifest.attributes.id) {
                return this.nodeManifest.attributes.id;
            }

            return manifest.getRandomizedIdString();
        },
        /**
         * @returns {boolean} the text string for the class.
         */
        class() {
            return this.getAttribute('class', '');
        },

    },
    methods: {
        /**
         * Gets an attribute name formatted correctly for property name
         * e.g. 'xXXX' -> 'dataXXXX'
         * @param {string} attributeName The name of the property
         * @returns {string} the name formatted as a property.
         */
        getAttributeNameFormattedAsPropertyName(attributeName) {
            let capitalized = attributeName.charAt(0).toUpperCase() + attributeName.slice(1);
            return `data${capitalized}`;
        },

        /**
         * Gets an attribute
         * @param {string} attributeName The name of the property
         * @param {string} defaultValue The default value
         * @param {Error} error The error to throw if property does not exist.
         * @returns {string} the attribute property
         */
        getAttribute(attributeName, defaultValue, error) {
            let formattedPropName = this.getAttributeNameFormattedAsPropertyName(attributeName);
            if (this.hasPropValue(formattedPropName)) {
                return this.getPropValue(formattedPropName);
            }

            if (this.hasPropValue('nodeManifest') && this.nodeManifest.attributes[attributeName]) {
                let attribute = this.nodeManifest.attributes[attributeName];
                if (attribute) {
                    return attribute;
                }
            }

            if (error) {
                throw error;
            }

            return defaultValue;
        },
        /**
         * Gets a property
         * @param {string} propName The name of the property
         * @returns {*} the prop value.
         */
        getPropValue(propName) {
            return this[propName];
        },
        /**
         * Gets whether the property has a value
         * @param {string} propName The name of the property
         * @returns {Boolean} whether the property has a value.
         */
        hasPropValue(propName) {
            //eslint-disable-next-line array-bracket-spacing
            return Object.entries(this.$props).find(([key, value, ]) =>
                value !== undefined && key === propName) !== undefined;
        },
        /**
         * The list of options
         * @returns {*} array of options.
         */
        getOptions() {
            let optionsString = 'dataOptions';
            let options = [];
            if (this.hasPropValue(optionsString)) {
                options = this[optionsString];
            }

            if (this.hasPropValue('nodeManifest')) {
                options = this.nodeManifest.options;
            }

            // check for a placeholder message
            let placeholder = this.getAttribute('placeholder', '');
            if (placeholder) {

                // add the placeholder if it hasn't been added yet
                let existing = options.find((o) => o.value === '');
                if (!existing) {
                    options.unshift({
                        text: placeholder,
                        value: '',
                    });
                }
            }

            return options;
        },
        /**
         * Maps a function name onto a registered function
         * @param {string} interactionFunctionString The name of the function
         * @returns {function} the function.
         */
        convertInteractionFunction(interactionFunctionString) {
            if (typeof interactionFunctionString === 'string') {
                let funcName = Object.keys(this.applicationInstance.interactionFunctionMap).find(
                    (key) => key === interactionFunctionString
                );

                if (funcName) {
                    let func = this.applicationInstance.interactionFunctionMap[funcName];
                    func = func.bind(this, this.$store, this.$router);
                    return func;
                }

                console.warn(
                    `'${interactionFunctionString}' is not a Custom Interaction Function function!`
                );
                return null; // caller needs to handle null
            }
            return null;
        },
    },
};



// WEBPACK FOOTER //
// ./source/framework/mixins/abstractElement.js